import { useState, useEffect, useCallback, useRef } from 'react'
import { 
  Users, 
  Network, 
  MapPin, 
  Layers,
  RefreshCw,
  Plus,
  List
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { OrgChartService, TeamMember, Team, RoleWithMember, RoleDefinition, OrgChartVersion } from '../services/orgChartService'
import { LoadingSpinner, ErrorAlert } from '../components/shared'
import { OrgChartView } from '../components/OrgChart/OrgChartView'
import { DirectoryView } from '../components/OrgChart/DirectoryView'
import { LocationView } from '../components/OrgChart/LocationView'
import { TeamsView } from '../components/OrgChart/TeamsView'
import { RoleModal } from '../components/OrgChart/RoleModal'
import { AssignPersonModal } from '../components/OrgChart/AssignPersonModal'
import { CreateTeamModal } from '../components/OrgChart/CreateTeamModal'
import { SyncConflictModal } from '../components/OrgChart/SyncConflictModal'
import { PositionDescriptionModal } from '../components/OrgChart/PositionDescriptionModal'
import { OrgChartSwitcher } from '../components/OrgChart/OrgChartSwitcher'
import { ManageRolesModal } from '../components/OrgChart/ManageRolesModal'
import { getFunctions, httpsCallable } from 'firebase/functions'

type ViewMode = 'orgchart' | 'directory' | 'location' | 'teams'

interface SyncConflict {
  memberId: string
  memberName: string
  memberEmail: string
  conflicts: Array<{
    field: string
    existingValue: any
    googleValue: any
  }>
}

export default function TeamBrevity() {
  const { isAdmin } = useAuth()
  const [viewMode, setViewMode] = useState<ViewMode>('orgchart')
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState('')

  // Data state
  const [roles, setRoles] = useState<RoleWithMember[]>([])
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [roleDefinitions, setRoleDefinitions] = useState<RoleDefinition[]>([])
  const [orgCharts, setOrgCharts] = useState<OrgChartVersion[]>([])
  const [activeChartId, setActiveChartId] = useState<string | null>(null)
  const [chartsBusy, setChartsBusy] = useState(false)

  // Modal state
  const [roleModalOpen, setRoleModalOpen] = useState(false)
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [createTeamModalOpen, setCreateTeamModalOpen] = useState(false)
  const [syncConflictModalOpen, setSyncConflictModalOpen] = useState(false)
  
  // Edit state
  const [editingRole, setEditingRole] = useState<RoleWithMember | null>(null)
  const [parentRoleForNew, setParentRoleForNew] = useState<string | null>(null)
  const [copiedRoleData, setCopiedRoleData] = useState<{
    roleDefinitionId: string | null
    title: string
    teams: string[]
    teamColors: string[]
    reportsToRoleId: string | null
    accountabilities: string[]
    order: number
  } | null>(null)
  const [selectedRoleForAssign, setSelectedRoleForAssign] = useState<RoleWithMember | null>(null)
  const [positionDescriptionRole, setPositionDescriptionRole] = useState<RoleWithMember | null>(null)
  const [manageRolesOpen, setManageRolesOpen] = useState(false)
  const [syncConflicts, setSyncConflicts] = useState<SyncConflict[]>([])

  const orgChartService = OrgChartService.getInstance()
  const functions = getFunctions()
  const hasLoadedOnce = useRef(false)
  const activeChartIdRef = useRef<string | null>(null)

  // Load data
  const loadData = useCallback(async (chartIdOverride?: string | null) => {
    try {
      // Only show the full-page spinner on the first load so org-chart zoom/focus
      // are preserved across create/edit/assign refreshes.
      if (!hasLoadedOnce.current) {
        setLoading(true)
      }
      setError('')

      if (isAdmin) {
        try {
          await orgChartService.ensureOrgChartsMigrated()
        } catch (migrateErr) {
          console.warn('Org chart migration skipped:', migrateErr)
        }
        try {
          await orgChartService.migrateLegacyRoleDefinitions()
        } catch (migrateErr) {
          console.warn('Role definition migration skipped:', migrateErr)
        }
      }

      const charts = await orgChartService.getAllOrgCharts()
      // Non-admins only ever see the live chart (no draft list)
      const visibleCharts = isAdmin
        ? charts
        : charts.filter(c => c.status === 'live')
      setOrgCharts(visibleCharts)

      const live = charts.find(c => c.status === 'live') || null
      let chartId =
        chartIdOverride !== undefined ? chartIdOverride : activeChartIdRef.current

      // Non-admins always view the live chart
      if (!isAdmin) {
        chartId = live?.id ?? null
      } else if (!chartId || !charts.some(c => c.id === chartId)) {
        chartId = live?.id ?? charts[0]?.id ?? null
      }

      activeChartIdRef.current = chartId
      setActiveChartId(chartId)

      const [rolesData, membersData, teamsData, definitionsData] = await Promise.all([
        chartId
          ? orgChartService.getRolesWithMembers(chartId)
          : Promise.resolve([] as RoleWithMember[]),
        orgChartService.getAllTeamMembers(),
        orgChartService.getAllTeams(),
        orgChartService.getAllRoleDefinitions()
      ])

      setRoles(rolesData)
      setTeamMembers(membersData)
      setTeams(teamsData)
      setRoleDefinitions(definitionsData)
      hasLoadedOnce.current = true
    } catch (err: any) {
      console.error('Error loading Team Brevity data:', err)
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [isAdmin])

  const refreshRoleDefinitions = useCallback(async () => {
    const definitionsData = await orgChartService.getAllRoleDefinitions()
    setRoleDefinitions(definitionsData)
    const chartId = activeChartIdRef.current
    if (chartId) {
      const rolesData = await orgChartService.getRolesWithMembers(chartId)
      setRoles(rolesData)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleSelectChart = async (chartId: string) => {
    setActiveChartId(chartId)
    activeChartIdRef.current = chartId
    await loadData(chartId)
  }

  const handleCreateDraft = async () => {
    if (!isAdmin || !activeChartId) return
    const source = orgCharts.find(c => c.id === activeChartId)
    const defaultName = source
      ? `${source.name} (draft)`
      : 'New draft'
    const name = window.prompt('Name for the new draft', defaultName)
    if (name == null || !name.trim()) return

    try {
      setChartsBusy(true)
      setError('')
      const draftId = await orgChartService.createDraftFromChart(
        activeChartId,
        name.trim()
      )
      await loadData(draftId)
    } catch (err: any) {
      console.error('Error creating draft:', err)
      setError(err.message || 'Failed to create draft')
    } finally {
      setChartsBusy(false)
    }
  }

  const handlePromoteToLive = async () => {
    if (!isAdmin || !activeChartId) return
    const chart = orgCharts.find(c => c.id === activeChartId)
    if (!chart || chart.status !== 'draft') return
    if (
      !confirm(
        `Make “${chart.name}” the live org chart? The current live chart will be saved as a draft.`
      )
    ) {
      return
    }

    try {
      setChartsBusy(true)
      setError('')
      await orgChartService.promoteDraftToLive(activeChartId)
      await loadData(activeChartId)
    } catch (err: any) {
      console.error('Error promoting draft:', err)
      setError(err.message || 'Failed to make chart live')
    } finally {
      setChartsBusy(false)
    }
  }

  const handleRenameChart = async (chartId: string, name: string) => {
    if (!isAdmin) return
    try {
      setChartsBusy(true)
      await orgChartService.renameOrgChart(chartId, name)
      const charts = await orgChartService.getAllOrgCharts()
      setOrgCharts(charts)
    } catch (err: any) {
      console.error('Error renaming chart:', err)
      setError(err.message || 'Failed to rename chart')
    } finally {
      setChartsBusy(false)
    }
  }

  const handleDeleteDraft = async (chartId: string) => {
    if (!isAdmin) return
    try {
      setChartsBusy(true)
      setError('')
      await orgChartService.deleteDraftChart(chartId)
      const live = (await orgChartService.getAllOrgCharts()).find(c => c.status === 'live')
      await loadData(live?.id ?? null)
    } catch (err: any) {
      console.error('Error deleting draft:', err)
      setError(err.message || 'Failed to delete draft')
    } finally {
      setChartsBusy(false)
    }
  }

  // Bidirectional sync with Google Directory
  const handleSyncFromDirectory = async () => {
    if (!isAdmin) return

    try {
      setSyncing(true)
      setError('')

      const syncFunc = httpsCallable(functions, 'syncTeamMembersFromDirectory')
      const result = await syncFunc({})
      const data = result.data as {
        success: boolean
        createdInFirebase: number
        updatedInFirebase: number
        updatedInGoogle: number
        skipped: number
        conflicts: SyncConflict[]
      }

      if (data.conflicts && data.conflicts.length > 0) {
        setSyncConflicts(data.conflicts)
        setSyncConflictModalOpen(true)
      }

      // Reload data to show updates
      await loadData()

      console.log(`Sync complete: ${data.createdInFirebase} created, ${data.updatedInFirebase} updated in Firebase, ${data.updatedInGoogle} pushed to Google`)
    } catch (err: any) {
      console.error('Error syncing directory:', err)
      setError(err.message || 'Failed to sync directory')
    } finally {
      setSyncing(false)
    }
  }

  // Role operations
  const handleCreateRole = (parentId: string | null = null) => {
    if (!isAdmin) return
    setEditingRole(null)
    setParentRoleForNew(parentId)
    setCopiedRoleData(null)
    setRoleModalOpen(true)
  }

  const handleEditRole = (role: RoleWithMember) => {
    if (!isAdmin) return
    setEditingRole(role)
    setParentRoleForNew(null)
    setCopiedRoleData(null)
    setRoleModalOpen(true)
  }

  const handleCopyRole = (role: RoleWithMember) => {
    if (!isAdmin) return
    // Copy card linkage but make it vacant (no assignedMemberId)
    setCopiedRoleData({
      roleDefinitionId: role.roleDefinitionId,
      title: role.title,
      teams: role.teams || [],
      teamColors: role.teamColors || [],
      reportsToRoleId: role.reportsToRoleId,
      accountabilities: role.accountabilities || [],
      order: role.order ?? 0
    })
    setEditingRole(null)
    setParentRoleForNew(null)
    setRoleModalOpen(true)
  }

  const handleRoleSaved = async () => {
    // Load data first, then close modal to prevent stale state
    await loadData()
    setRoleModalOpen(false)
    setEditingRole(null)
    setParentRoleForNew(null)
    setCopiedRoleData(null)
  }

  // Assign person to role
  const handleAssignPerson = (role: RoleWithMember) => {
    if (!isAdmin) return
    setSelectedRoleForAssign(role)
    setAssignModalOpen(true)
  }

  const handleViewPositionDescription = (role: RoleWithMember) => {
    setPositionDescriptionRole(role)
  }

  const getReportsToLabel = (role: RoleWithMember): string | null => {
    if (!role.reportsToRoleId) return null
    const parent = roles.find(r => r.id === role.reportsToRoleId)
    if (!parent) return null
    const parentName = parent.assignedMember?.name.fullName
    return parentName
      ? `${parent.title} (${parentName})`
      : `${parent.title} (Vacant)`
  }

  const handlePersonAssigned = async () => {
    await loadData()
    setAssignModalOpen(false)
    setSelectedRoleForAssign(null)
  }

  // Team operations
  const handleCreateTeam = () => {
    if (!isAdmin) return
    setCreateTeamModalOpen(true)
  }

  const handleTeamCreated = async () => {
    await loadData()
    setCreateTeamModalOpen(false)
  }

  // Conflict resolution
  const handleConflictsResolved = async () => {
    setSyncConflictModalOpen(false)
    setSyncConflicts([])
    await loadData()
  }

  const viewTabs = [
    { id: 'orgchart' as ViewMode, label: 'Org Chart', icon: Network },
    { id: 'directory' as ViewMode, label: 'Directory', icon: Users },
    { id: 'location' as ViewMode, label: 'Location', icon: MapPin },
    { id: 'teams' as ViewMode, label: 'Teams', icon: Layers }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Users className="h-6 w-6 text-purple-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Team Brevity</h1>
                <p className="text-gray-600">What you do here</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Admin-only sync button */}
              {isAdmin && (
                <button
                  onClick={handleSyncFromDirectory}
                  disabled={syncing}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                    syncing
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                  <span className="text-sm font-medium">
                    {syncing ? 'Syncing...' : 'Sync Directory'}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* View Toggle Tabs */}
          <div className="mt-4 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {viewTabs.map((tab) => {
                const Icon = tab.icon
                const isActive = viewMode === tab.id
                return (
                  <button
                    key={tab.id}
                    onClick={() => setViewMode(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      isActive
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                )
              })}
            </nav>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <LoadingSpinner message="Loading Team Brevity..." fullPage />
        ) : error ? (
          <ErrorAlert
            title="Error Loading Data"
            message={error}
            onRetry={loadData}
            fullPage
          />
        ) : (
          <>
            {viewMode === 'orgchart' && roles.length === 0 && (
              <div className="min-h-[400px]">
                {isAdmin && (
                  <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                    <OrgChartSwitcher
                      charts={orgCharts}
                      activeChartId={activeChartId}
                      isAdmin={isAdmin}
                      busy={chartsBusy}
                      onSelect={handleSelectChart}
                      onCreateDraft={handleCreateDraft}
                      onPromoteToLive={handlePromoteToLive}
                      onRename={handleRenameChart}
                      onDeleteDraft={handleDeleteDraft}
                    />
                    <button
                      type="button"
                      onClick={() => setManageRolesOpen(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                    >
                      <List className="w-4 h-4" />
                      Manage roles
                    </button>
                  </div>
                )}
                <div className="flex flex-col items-center justify-center py-16">
                  <Network className="w-16 h-16 text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Roles Yet</h3>
                  <p className="text-gray-500 mb-4">
                    {isAdmin
                      ? 'Create your first role card to start building this org chart'
                      : 'This org chart has no roles yet'}
                  </p>
                  {isAdmin && (
                    <button
                      onClick={() => handleCreateRole(null)}
                      className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Create First Role Card</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* View Content */}
            {viewMode === 'orgchart' && roles.length > 0 && (
              <OrgChartView
                roles={roles}
                teams={teams}
                isAdmin={isAdmin}
                orgCharts={orgCharts}
                activeChartId={activeChartId}
                chartsBusy={chartsBusy}
                onSelectChart={handleSelectChart}
                onCreateDraft={handleCreateDraft}
                onPromoteToLive={handlePromoteToLive}
                onRenameChart={handleRenameChart}
                onDeleteDraft={handleDeleteDraft}
                onManageRoles={() => setManageRolesOpen(true)}
                onCreateRole={handleCreateRole}
                onEditRole={handleEditRole}
                onCopyRole={handleCopyRole}
                onAssignPerson={handleAssignPerson}
                onViewPositionDescription={handleViewPositionDescription}
              />
            )}

            {viewMode === 'directory' && (
              <DirectoryView
                teamMembers={teamMembers}
                roles={roles}
                isAdmin={isAdmin}
                onMemberUpdated={loadData}
              />
            )}

            {viewMode === 'location' && (
              <LocationView
                teamMembers={teamMembers}
                roles={roles}
                isAdmin={isAdmin}
                onUpdateLocation={loadData}
              />
            )}

            {viewMode === 'teams' && (
              <TeamsView
                teams={teams}
                roles={roles}
                isAdmin={isAdmin}
                onCreateTeam={handleCreateTeam}
                onRefresh={loadData}
              />
            )}
          </>
        )}
      </main>

      {/* Modals */}
      {roleModalOpen && activeChartId && (
        <RoleModal
          isOpen={roleModalOpen}
          onClose={() => {
            setRoleModalOpen(false)
            setEditingRole(null)
            setParentRoleForNew(null)
            setCopiedRoleData(null)
          }}
          onSave={handleRoleSaved}
          role={editingRole}
          parentRoleId={parentRoleForNew}
          orgChartId={activeChartId}
          teams={teams}
          roles={roles}
          roleDefinitions={roleDefinitions}
          onRoleDefinitionsChanged={refreshRoleDefinitions}
          copiedRoleData={copiedRoleData}
        />
      )}

      {assignModalOpen && selectedRoleForAssign && (
        <AssignPersonModal
          isOpen={assignModalOpen}
          onClose={() => {
            setAssignModalOpen(false)
            setSelectedRoleForAssign(null)
          }}
          onAssign={handlePersonAssigned}
          role={selectedRoleForAssign}
          teamMembers={teamMembers}
          roles={roles}
        />
      )}

      {positionDescriptionRole && (
        <PositionDescriptionModal
          isOpen={!!positionDescriptionRole}
          onClose={() => setPositionDescriptionRole(null)}
          role={positionDescriptionRole}
          reportsToLabel={getReportsToLabel(positionDescriptionRole)}
        />
      )}

      {manageRolesOpen && (
        <ManageRolesModal
          isOpen={manageRolesOpen}
          onClose={() => setManageRolesOpen(false)}
          onChanged={refreshRoleDefinitions}
        />
      )}

      {createTeamModalOpen && (
        <CreateTeamModal
          isOpen={createTeamModalOpen}
          onClose={() => setCreateTeamModalOpen(false)}
          onSave={handleTeamCreated}
        />
      )}

      {syncConflictModalOpen && syncConflicts.length > 0 && (
        <SyncConflictModal
          isOpen={syncConflictModalOpen}
          onClose={() => {
            setSyncConflictModalOpen(false)
            setSyncConflicts([])
          }}
          onResolve={handleConflictsResolved}
          conflicts={syncConflicts}
        />
      )}
    </div>
  )
}
